import React, { useEffect, useState } from 'react';
import ApplicationLogo from '@/Components/ApplicationLogo.jsx';
import Dropdown from '@/Components/Dropdown.jsx';
import ResponsiveNavLink from '@/Components/ResponsiveNavLink.jsx';
import { usePage } from '@inertiajs/react';
import { toast, ToastContainer } from 'react-toastify';
import DarkLightModeSwitcher from '@/Components/DarkLightModeSwitcher.jsx';
import { Modal } from 'flowbite-react';
import Feedback from '@/Components/Feedback.jsx';
import { DataProvider } from '@/Components/DataContext.jsx';
import MyFooter from '@/Layouts/Partials/Footer.jsx';
import OpenResumesModal from '@/Pages/Resume/Partials/Modals/OpenResumesModal';
import AlertsContainer from '@/Components/AlertsContainer.jsx';
import MoreAIJobToolsDropdown from '@/Layouts/Partials/MoreAIJobToolsDropdown.jsx';
import LinkExternal from '@/Components/LinkExternal.jsx';
import ImpersonationBanner from '@/Components/ImpersonationBanner.jsx';

export default function App({ user, header, children }) {
  return (
    <AppLayout user={user} header={header}>
      {children}
    </AppLayout>
  );
}

function AppLayout({ user, header, children }) {
  const { props } = usePage(); // Accessing Inertia page props
  const [isOpenResumesModalOpen, setIsOpenResumesModalOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('ref') && props.auth.user && !props.auth.user.referred_by) {
      fetch('/ref/associate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ref: localStorage.getItem('ref'),
        }),
      })
        .then((response) => {
          if (!response.ok) {
            // If response indicates an error (like invalid referral code)
            if (response.status === 400) {
              // Clear the invalid referral code from localStorage
              localStorage.removeItem('ref');
              console.warn('Invalid referral code removed from localStorage');
            }
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          console.log('Success:', data);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  }, [props.auth.user]);

  useEffect(() => {
    if (props.flash && props.flash.success) {
      toast.success(props.flash.success);
    }
    if (props.flash && props.flash.warning) {
      toast.warning(props.flash.warning);
    }
    if (props.flash && props.flash.error) {
      toast.error(props.flash.error);
    }
    if (props.flash && props.flash.info) {
      toast.info(props.flash.info);
    }
  }, [props]);

  const [showingNavigationDropdown, setShowingNavigationDropdown] = useState(false);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const handleFeedbackCloseButtonClick = () => {
    setIsFeedbackModalOpen(false);
  };

  return (
    <DataProvider>
      <div
        className={`min-h-screen bg-gray-100 dark:bg-gray-900 ${showingNavigationDropdown ? 'overflow-hidden md:overflow-auto' : ''}`}
      >
        <ImpersonationBanner
          isImpersonating={props.isImpersonating}
          originalAdmin={props.originalAdmin}
        />
        <AlertsContainer />

        <header className="antialiased">
          {/* Desktop View */}
          <div className="hidden md:block">
            {/* Second Level: Logo and User Menu */}
            <nav className="border-gray-200 px-4 lg:px-6 pt-2.5 dark:bg-gray-900">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                <div className="flex items-center">
                  {/* Logo */}
                  <LinkExternal
                    href={process.env.NODE_ENV === 'development' ? '/' : 'https://sheetsresume.com'}
                    className="flex items-center plain-link"
                  >
                    <ApplicationLogo className="block h-9 w-auto fill-current text-gray-800 dark:text-gray-200" />
                    <span className="ml-3 text-lg text-gray-800 dark:text-white">
                      Sheets Resume
                    </span>
                  </LinkExternal>
                </div>

                <div className="flex items-center space-x-4">
                  {/* User Dropdown and Dark Mode Switcher */}
                  {
                    <ul className="flex justify-between p-2">
                      <li className="lg:mr-4 font-bold">
                        <ResponsiveNavLink
                          href={route('builder')}
                          active={route().current('builder') || route().current('builder.section')}
                          fontBold={true}
                        >
                          My Resume
                        </ResponsiveNavLink>
                      </li>
                      <li className="lg:mr-4">
                        <ResponsiveNavLink
                          href={route('reviews')}
                          active={route().current('reviews')}
                        >
                          Reviews
                        </ResponsiveNavLink>
                      </li>
                      <li className="lg:mr-4">
                        <ResponsiveNavLink href={'https://sheetsresume.com/faq/'} external={true}>
                          FAQ
                        </ResponsiveNavLink>
                      </li>

                      <MoreAIJobToolsDropdown />
                    </ul>
                  }

                  <div className="relative">
                    <Dropdown>
                      <Dropdown.Trigger>
                        <span className="inline-flex rounded-md">
                          <button
                            type="button"
                            className="inline-flex items-center px-3 py-2 border border-transparent leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition ease-in-out duration-150"
                          >
                            {user ? user.first_name : 'Guest'}
                            <svg
                              className="ms-2 -me-0.5 h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </span>
                      </Dropdown.Trigger>

                      <Dropdown.Content>
                        {user ? (
                          <>
                            <Dropdown.Link href={route('profile.edit')}>My Account</Dropdown.Link>
                            <Dropdown.Link
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsOpenResumesModalOpen(true);
                              }}
                            >
                              My Resumes
                            </Dropdown.Link>
                            <Dropdown.Link href={route('referral.view')}>
                              My Referrals
                            </Dropdown.Link>
                            {user.is_admin === 1 && (
                              <Dropdown.Link href={route('admin')}>Admin</Dropdown.Link>
                            )}
                            <Dropdown.Link href={route('logout')} method="post" as="button">
                              Log Out
                            </Dropdown.Link>
                          </>
                        ) : (
                          <>
                            <Dropdown.Link href={route('login')}>Log in</Dropdown.Link>
                            <Dropdown.Link href={route('register')}>Register</Dropdown.Link>
                          </>
                        )}
                      </Dropdown.Content>
                    </Dropdown>
                  </div>
                  <DarkLightModeSwitcher />
                </div>
              </div>
            </nav>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <nav className="border-gray-200 px-4 lg:px-6 pt-2.5 pb-2 dark:bg-gray-900 flex justify-between items-center relative z-50">
              {/* Logo */}
              <LinkExternal
                href={process.env.NODE_ENV === 'development' ? '/' : 'https://sheetsresume.com'}
                className="flex items-center plain-link"
              >
                <ApplicationLogo className="block h-9 w-auto fill-current text-gray-800 dark:text-gray-200" />
                <span className="ml-3 text-lg text-gray-800 dark:text-white">Sheets Resume</span>
              </LinkExternal>

              <div className="flex items-center gap-2">
                {/* Dark/Light Mode Toggle */}
                <DarkLightModeSwitcher />

                {/* Hamburger Icon */}
                <button
                  onClick={() => setShowingNavigationDropdown(!showingNavigationDropdown)}
                  className={`inline-flex items-center justify-center p-2 rounded-md transition duration-150 ease-in-out ${
                    showingNavigationDropdown
                      ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-sm z-50 relative'
                      : 'text-gray-400 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-900 focus:text-gray-500 dark:focus:text-gray-300'
                  }`}
                  aria-label={showingNavigationDropdown ? 'Close menu' : 'Open menu'}
                >
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path
                      className={!showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                    <path
                      className={showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </nav>

            {/* Mobile Menu - Modal overlay */}
            <div
              className={
                showingNavigationDropdown
                  ? 'fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity'
                  : 'hidden'
              }
              onClick={() => setShowingNavigationDropdown(false)}
            ></div>

            {/* Mobile Menu */}
            <div
              className={
                showingNavigationDropdown
                  ? 'fixed inset-x-0 top-16 z-50 transform transition duration-200 ease-in-out'
                  : 'hidden'
              }
            >
              <div className="pt-2 pb-3 space-y-1 px-2">
                <ul className="rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900 shadow-md">
                  <li className="font-bold">
                    <ResponsiveNavLink
                      href={route('builder')}
                      active={route().current('builder') || route().current('builder.section')}
                      fontBold={true}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      Build My Resume
                    </ResponsiveNavLink>
                  </li>
                  <li>
                    <ResponsiveNavLink
                      href={route('reviews')}
                      active={route().current('reviews')}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      Reviews
                    </ResponsiveNavLink>
                  </li>
                  <li>
                    <ResponsiveNavLink
                      href={'https://sheetsresume.com/faq/'}
                      external={true}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      FAQ
                    </ResponsiveNavLink>
                  </li>
                  <li className="relative group">
                    <button
                      className="w-full flex items-center justify-between ps-3 pe-3 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:text-gray-800 dark:focus:text-gray-200 text-base font-medium focus:outline-none transition duration-150 ease-in-out"
                      onClick={(e) => {
                        e.preventDefault();
                        const dropdownEl = e.currentTarget.nextElementSibling;
                        if (dropdownEl.classList.contains('hidden')) {
                          dropdownEl.classList.remove('hidden');
                        } else {
                          dropdownEl.classList.add('hidden');
                        }
                      }}
                    >
                      More AI Job Tools
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <div className="hidden bg-gray-50 dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      <ul>
                        <li>
                          <ResponsiveNavLink
                            href={route('builder')}
                            active={route().current('builder.section')}
                            className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            AI Resume Builder
                          </ResponsiveNavLink>
                        </li>
                        <li>
                          <ResponsiveNavLink
                            href={route('cover-letter')}
                            active={route().current('cover-letter')}
                            className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2 text-green-600 dark:text-green-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            AI Cover Letter Maker
                          </ResponsiveNavLink>
                        </li>
                        <li>
                          <ResponsiveNavLink
                            href={route('mock-interview')}
                            active={route().current('mock-interview')}
                            className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                              />
                            </svg>
                            AI Mock Interview
                          </ResponsiveNavLink>
                        </li>
                        <li>
                          <ResponsiveNavLink
                            href={route('jobs')}
                            active={route().current('jobs')}
                            className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2 text-amber-600 dark:text-amber-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            AI Job Board
                          </ResponsiveNavLink>
                        </li>
                        <li>
                          <ResponsiveNavLink
                            href={route('tools')}
                            active={route().current('tools')}
                            className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 font-medium text-blue-600 dark:text-blue-300 flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-300"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4 6h16M4 12h16m-7 6h7"
                              />
                            </svg>
                            View All Tools
                          </ResponsiveNavLink>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <li>
                    <ResponsiveNavLink
                      href={'https://sheetsresume.com/advice/'}
                      external={true}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      Advice
                    </ResponsiveNavLink>
                  </li>
                  <li>
                    <ResponsiveNavLink
                      href={'https://sheetsresume.com/resume-template/'}
                      external={true}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      Free Resume Template
                    </ResponsiveNavLink>
                  </li>
                  <li>
                    <ResponsiveNavLink
                      href={'https://sheetsresume.com/about/'}
                      external={true}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 py-3"
                    >
                      About
                    </ResponsiveNavLink>
                  </li>
                  <li className="relative group">
                    <button
                      className="w-full flex items-center justify-between ps-3 pe-3 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:text-gray-800 dark:focus:text-gray-200 text-base font-medium focus:outline-none transition duration-150 ease-in-out"
                      onClick={(e) => {
                        e.preventDefault();
                        const dropdownEl = e.currentTarget.nextElementSibling;
                        if (dropdownEl.classList.contains('hidden')) {
                          dropdownEl.classList.remove('hidden');
                        } else {
                          dropdownEl.classList.add('hidden');
                        }
                      }}
                    >
                      {user ? user.first_name : 'Guest'}
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {/* User Links in Mobile */}
                    <div className="hidden bg-gray-50 dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      <ul>
                        {user ? (
                          <>
                            <li>
                              <ResponsiveNavLink
                                href={route('profile.edit')}
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                                My Account
                              </ResponsiveNavLink>
                            </li>
                            <li>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsOpenResumesModalOpen(true);
                                  setShowingNavigationDropdown(false);
                                }}
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:text-gray-800 dark:focus:text-gray-200 text-base font-medium focus:outline-none transition duration-150 ease-in-out"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                My Resumes
                              </a>
                            </li>
                            <li>
                              <ResponsiveNavLink
                                href={route('referral.view')}
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-green-600 dark:text-green-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                                My Referrals
                              </ResponsiveNavLink>
                            </li>
                            {user.is_admin === 1 && (
                              <li>
                                <ResponsiveNavLink
                                  href={route('admin')}
                                  className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                  </svg>
                                  Admin
                                </ResponsiveNavLink>
                              </li>
                            )}
                            <li>
                              <ResponsiveNavLink
                                method="post"
                                href={route('logout')}
                                as="button"
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 w-full text-left flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-red-600 dark:text-red-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                  />
                                </svg>
                                Log Out
                              </ResponsiveNavLink>
                            </li>
                          </>
                        ) : (
                          <>
                            <li>
                              <ResponsiveNavLink
                                href={route('login')}
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-green-600 dark:text-green-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                                  />
                                </svg>
                                Log in
                              </ResponsiveNavLink>
                            </li>
                            <li>
                              <ResponsiveNavLink
                                href={route('register')}
                                className="pl-6 hover:bg-gray-100 dark:hover:bg-gray-700 py-3 flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-300"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                  />
                                </svg>
                                Register
                              </ResponsiveNavLink>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </header>

        {header && 1 === 2 && (
          <header className="bg-white dark:bg-gray-800 shadow">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">{header}</div>
          </header>
        )}

        <main>{children}</main>

        <MyFooter setIsFeedbackModalOpen={setIsFeedbackModalOpen} />

        <Modal
          position="center"
          show={isFeedbackModalOpen}
          onClose={handleFeedbackCloseButtonClick}
          theme={{
            root: {
              base: 'fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full',
              show: {
                on: 'flex bg-gray-900 bg-opacity-50 dark:bg-opacity-80',
                off: 'hidden',
              },
            },
            content: {
              base: 'relative h-full w-full p-4 md:h-auto',
              inner:
                'relative rounded-lg bg-white shadow dark:bg-gray-800 flex flex-col max-h-[90vh]',
            },
          }}
        >
          <Modal.Header className="text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700">
            Submit Feedback
          </Modal.Header>
          <Modal.Body className="dark:bg-gray-800">
            <Feedback showLayout={false} setIsFeedbackModalOpen={setIsFeedbackModalOpen} />
          </Modal.Body>
        </Modal>

        {/* Open Resumes Modal */}
        <OpenResumesModal
          setModalOpen={setIsOpenResumesModalOpen}
          modalOpen={isOpenResumesModalOpen}
          filters={{ pageSize: 10 }}
        />

        <ToastContainer
          position="top-right"
          toastClassName="dark:bg-gray-800 dark:text-gray-200"
          theme={document?.documentElement?.classList?.contains('dark') ? 'dark' : 'light'}
        />
      </div>
    </DataProvider>
  );
}
